import { useEffect, useState, useCallback } from "react";
import { Search, Download, Copy, ArrowRight, Trash2, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { authFetch, apiErr } from "../../lib/adminApi";
import { AdminBar } from "./AdminCore";

const inputCls = "w-full bg-transparent border-b border-white/25 focus:border-white outline-none text-white py-2 text-sm";
const labelCls = "font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-2";

const SOURCE_LABELS = { static: "Core page", cms: "CMS page", area: "Area page" };

function RowEditor({ row, onSaved }) {
  const [f, setF] = useState({ title: row.title || "", description: row.description || "", h1: row.h1 || "", canonical: row.canonical || "", noindex: !!row.noindex });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const res = await authFetch("/api/seo/meta", { method: "PUT", body: JSON.stringify({ path: row.path, ...f }) });
      const data = await res.json();
      if (!res.ok) { toast.error(apiErr(data.detail)); return; }
      toast.success(`SEO saved for ${row.path}`);
      sessionStorage.removeItem("cv-seo");
      onSaved({ ...row, ...f });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white/[0.03] px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`seo-editor${row.path.replaceAll("/", "-")}`}>
      <div>
        <label className={labelCls}>SEO title <span className="text-white/30 normal-case tracking-normal">({f.title.length}/60)</span></label>
        <input value={f.title} onChange={set("title")} data-testid="seo-title-input" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>H1 heading</label>
        <input value={f.h1} onChange={set("h1")} data-testid="seo-h1-input" className={inputCls} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Meta description <span className="text-white/30 normal-case tracking-normal">({f.description.length}/160)</span></label>
        <textarea value={f.description} onChange={set("description")} rows={2} data-testid="seo-description-input" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Canonical URL <span className="text-white/30 normal-case tracking-normal">(blank = this page)</span></label>
        <input value={f.canonical} onChange={set("canonical")} placeholder="Leave blank — this page is its own canonical" data-testid="seo-canonical-input" className={inputCls} />
      </div>
      <div className="flex items-end justify-between gap-4">
        <label className="flex items-center gap-3 text-sm text-white/70 cursor-pointer pb-2">
          <input type="checkbox" checked={f.noindex} onChange={set("noindex")} data-testid="seo-noindex-checkbox" className="accent-[#5B7F9E] w-4 h-4" />
          Hide from Google (noindex)
        </label>
        <button onClick={save} disabled={busy} data-testid="seo-save-btn" className="btn-solid disabled:opacity-40">
          {busy ? "Saving…" : "Save SEO"}
        </button>
      </div>
    </div>
  );
}

function RedirectsTab() {
  const [redirects, setRedirects] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/redirects`).then((r) => r.json()).then(setRedirects).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authFetch("/api/redirects", { method: "POST", body: JSON.stringify({ from_path: from, to_path: to }) });
      const data = await res.json();
      if (!res.ok) { toast.error(apiErr(data.detail)); return; }
      toast.success("Redirect saved");
      sessionStorage.removeItem("cv-seo");
      setFrom(""); setTo(""); load();
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    const res = await authFetch(`/api/redirects?from_path=${encodeURIComponent(p)}`, { method: "DELETE" });
    if (res.ok) { toast.success("Redirect removed"); sessionStorage.removeItem("cv-seo"); load(); }
    else toast.error("Could not remove redirect");
  };

  return (
    <div data-testid="redirects-tab">
      <form onSubmit={add} className="border border-white/12 p-8 grid grid-cols-1 md:grid-cols-3 gap-5 items-end mb-10" data-testid="redirect-add-form">
        <div>
          <label className={labelCls}>Old URL path *</label>
          <input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="/old-page/" data-testid="redirect-from-input" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Redirects to *</label>
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="/windows/aluminium-windows/" data-testid="redirect-to-input" className={inputCls} />
        </div>
        <button type="submit" disabled={busy || !from.trim() || !to.trim()} data-testid="redirect-add-btn" className="btn-solid justify-center disabled:opacity-40">
          <Plus size={14} /> Add redirect
        </button>
      </form>
      <div className="border border-white/12 divide-y divide-white/10" data-testid="redirects-list">
        {redirects.map((r) => (
          <div key={r.from_path} className="flex items-center justify-between px-6 py-4 gap-4">
            <div className="flex items-center gap-3 min-w-0 text-sm">
              <span className="font-mono-x text-white/70 truncate">{r.from_path}</span>
              <ArrowRight size={14} className="text-[#5B7F9E] shrink-0" />
              <span className="font-mono-x text-white truncate">{r.to_path}</span>
            </div>
            <button onClick={() => remove(r.from_path)} data-testid={`redirect-delete${r.from_path.replaceAll("/", "-")}`}
              className="p-2 text-white/50 hover:text-red-400 transition-colors" title="Delete redirect">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {redirects.length === 0 && <p className="px-6 py-8 text-white/40 text-sm">No redirects yet. Old URLs that changed can be pointed at their new home here.</p>}
      </div>
    </div>
  );
}

export default function AdminSeo() {
  const [rows, setRows] = useState([]);
  const [sitemap, setSitemap] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState("pages");

  useEffect(() => {
    document.title = "Admin · SEO | Cedar Views";
    authFetch("/api/seo/urls").then((r) => r.json()).then((d) => { setRows(d.rows || []); setSitemap(d.sitemap || ""); }).catch(() => {});
  }, []);

  const copySitemap = () => { navigator.clipboard.writeText(sitemap); toast.success("Sitemap URL copied"); };

  const downloadCsv = async () => {
    const res = await authFetch("/api/seo/urls?format=csv");
    if (!res.ok) { toast.error("Could not export CSV"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "cedar-views-urls.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filtered = rows.filter((r) => !q || r.path.includes(q.toLowerCase()) || (r.title || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-seo">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title="SEO Manager." />

        <div className="border border-white/12 p-8 mb-10 flex flex-wrap items-center justify-between gap-6" data-testid="seo-sitemap-card">
          <div className="min-w-0">
            <p className={labelCls}>XML sitemap — live URL for Google Search Console</p>
            <p className="font-mono-x text-sm text-white truncate" data-testid="seo-sitemap-url">{sitemap}</p>
            <p className="text-white/40 text-xs mt-2">{rows.length} live URLs · robots.txt is served at /robots.txt</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={copySitemap} data-testid="seo-copy-sitemap-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 hover:bg-white hover:text-[#0B162C] text-xs font-semibold tracking-[0.12em] uppercase transition-colors">
              <Copy size={13} /> Copy URL
            </button>
            <button onClick={downloadCsv} data-testid="seo-export-csv-btn" className="btn-solid">
              <Download size={14} /> Export URL list (CSV)
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-8">
          {[["pages", "Page SEO"], ["redirects", "Redirects"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`seo-tab-${k}`}
              className={`px-5 py-2 text-xs font-semibold tracking-[0.15em] uppercase border transition-colors ${tab === k ? "bg-white text-[#0B162C] border-white" : "border-white/20 text-white/60 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "redirects" ? <RedirectsTab /> : (
          <>
            <div className="relative mb-6">
              <Search size={15} className="absolute left-0 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages by URL or title…" data-testid="seo-search-input"
                className={`${inputCls} pl-7`} />
            </div>
            <div className="border border-white/12 divide-y divide-white/10" data-testid="seo-url-list">
              {filtered.map((r) => (
                <div key={r.path}>
                  <button onClick={() => setOpen(open === r.path ? null : r.path)} data-testid={`seo-row${r.path.replaceAll("/", "-")}`}
                    className="w-full flex items-center justify-between px-6 py-4 gap-4 text-left hover:bg-white/[0.03] transition-colors">
                    <div className="min-w-0">
                      <p className="font-mono-x text-[12px] text-white truncate">{r.path}</p>
                      <p className="text-xs text-white/45 mt-1 truncate">{r.title || <span className="italic text-white/30">No title set — uses page default</span>}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.noindex && <span className="px-2 py-1 text-[10px] font-semibold tracking-wider uppercase bg-red-500/15 text-red-300">noindex</span>}
                      <span className="px-2 py-1 text-[10px] font-semibold tracking-wider uppercase bg-white/8 text-white/50">{SOURCE_LABELS[r.source] || r.source}</span>
                      <ChevronDown size={15} className={`text-white/40 transition-transform ${open === r.path ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {open === r.path && (
                    <RowEditor row={r} onSaved={(u) => setRows(rows.map((x) => (x.path === u.path ? u : x)))} />
                  )}
                </div>
              ))}
              {filtered.length === 0 && <p className="px-6 py-8 text-white/40 text-sm">No pages match your search.</p>}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
